import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { RestaurantLayout } from "@/components/tabdine/RestaurantLayout";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Analytics() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/restaurant/analytics").then((r) => setD(r.data));
  }, []);

  if (!d) return <RestaurantLayout><div className="p-6">Loading…</div></RestaurantLayout>;

  return (
    <RestaurantLayout>
      <div className="p-4 lg:p-8 max-w-6xl">
        <h1 className="font-heading text-2xl font-bold text-brand-dark mb-4">Analytics</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl p-4 border border-brand-dark/5">
            <div className="text-xs uppercase tracking-widest text-brand-dark/50">Revenue (all time)</div>
            <div className="font-heading text-2xl font-bold" data-testid="an-revenue">{inr(d.total_revenue)}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-brand-dark/5">
            <div className="text-xs uppercase tracking-widest text-brand-dark/50">Orders</div>
            <div className="font-heading text-2xl font-bold" data-testid="an-orders">{d.total_orders}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-brand-dark/5">
            <div className="text-xs uppercase tracking-widest text-brand-dark/50">Top item</div>
            <div className="font-heading text-lg font-bold truncate">{d.top_items[0]?.name || "—"}</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-brand-dark/5">
            <div className="text-xs uppercase tracking-widest text-brand-dark/50">Best day</div>
            <div className="font-heading text-lg font-bold">
              {DOW[[...d.dow_revenue].sort((a, b) => b.value - a.value)[0]?.day] || "—"}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-brand-dark/5 p-4">
            <div className="font-heading font-semibold mb-2">Revenue over time</div>
            <div className="h-56">
              <ResponsiveContainer>
                <LineChart data={d.revenue_over_time}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#C84B11" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-brand-dark/5 p-4">
            <div className="font-heading font-semibold mb-2">Peak hours</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={d.peak_hours}>
                  <XAxis dataKey="hour" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1A0A05" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-brand-dark/5 p-4">
            <div className="font-heading font-semibold mb-2">Top items</div>
            <div className="space-y-1.5">
              {d.top_items.map((it) => (
                <div key={it.name} className="flex justify-between text-sm">
                  <span className="text-brand-dark/80 truncate">{it.name}</span>
                  <span className="font-heading font-semibold">{it.count}</span>
                </div>
              ))}
              {d.top_items.length === 0 && <div className="text-xs text-brand-dark/50">No orders yet.</div>}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-brand-dark/5 p-4">
            <div className="font-heading font-semibold mb-2">Revenue by day of week</div>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={d.dow_revenue.map((x) => ({ ...x, day: DOW[x.day] }))}>
                  <XAxis dataKey="day" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#C84B11" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </RestaurantLayout>
  );
}
